import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserService } from './user/user.service';
import { WidgetPostService } from './widget-post.service';

/**
 * WidgetUserProxy
 * 
 * Extends UserService and overrides the methods used by PostComponent
 * to route them through the widget API (authenticated via widget JWT).
 * 
 * personId and token are set by WidgetPostComponent before ngOnInit runs.
 */
@Injectable()
export class WidgetUserProxy extends UserService {

  private _widgetPersonId = '';
  private _widgetToken = '';
  private _widgetSvc: WidgetPostService;

  constructor(http: HttpClient) {
    super(http);
    this._widgetSvc = new WidgetPostService(http);
  }

  /** Called by WidgetPostComponent constructor before ngOnInit */
  configureWidget(personId: string, token: string) {
    this._widgetPersonId = personId;
    this._widgetToken = token;
    console.log('[WidgetUserProxy] configureWidget:', { personId, tokenLength: token?.length, tokenPreview: token?.substring(0, 30) + '...' });
  }

  // ========================================================================
  // Overrides of UserService methods used by PostComponent
  // ========================================================================

  override getPersonbyCode = async (body: any) => {
    return this._widgetSvc.getPersonbyCode(this._widgetPersonId, this._widgetToken);
  };

  override account_payment = async (params: any = {}) => {
    console.log('[WidgetUserProxy] account_payment called:', { personId: this._widgetPersonId, hasToken: !!this._widgetToken, params });
    try {
      const resp = await this._widgetSvc.account_payment(this._widgetPersonId, this._widgetToken, params);
      console.log('[WidgetUserProxy] account_payment response:', resp);
      return resp;
    } catch (err: any) {
      console.error('[WidgetUserProxy] account_payment ERROR:', err?.status, err?.error || err?.message);
      throw err;
    }
  };

  override getRate = async (body: any = {}) => {
    return this._widgetSvc.getRate(this._widgetPersonId, this._widgetToken, body);
  };

  override getDeduction_payment = async (body: any = {}) => {
    return this._widgetSvc.getDeduction_payment(this._widgetPersonId, this._widgetToken);
  };

  override getBanks = async (params: any = {}) => {
    return this._widgetSvc.getBanks(this._widgetPersonId, this._widgetToken);
  };

  override getTypeProducts = async (params?: any) => {
    console.log('[WidgetUserProxy] getTypeProducts called:', { personId: this._widgetPersonId, hasToken: !!this._widgetToken });
    try {
      const resp = await this._widgetSvc.getTypeProducts(this._widgetPersonId, this._widgetToken);
      console.log('[WidgetUserProxy] getTypeProducts response:', resp);
      return resp;
    } catch (err: any) {
      console.error('[WidgetUserProxy] getTypeProducts ERROR:', err?.status, err?.error || err?.message);
      throw err;
    }
  };

  override getTypesProductsInvoices = async () => {
    console.log('[WidgetUserProxy] getTypesProductsInvoices called');
    try {
      const resp = await this._widgetSvc.getTypesProductsInvoices(this._widgetPersonId, this._widgetToken);
      console.log('[WidgetUserProxy] getTypesProductsInvoices response:', resp);
      return resp;
    } catch (err: any) {
      console.error('[WidgetUserProxy] getTypesProductsInvoices ERROR:', err?.status, err?.error || err?.message);
      throw err;
    }
  };

  override getAllCodes = async (params?: any) => {
    return this._widgetSvc.getAllCodes(this._widgetPersonId, this._widgetToken);
  };

  override getPersonInstallmentsInvoiceCredits = async (body: any) => {
    return this._widgetSvc.getPersonInstallmentsInvoiceCredits(
      this._widgetPersonId, this._widgetToken, body
    );
  };

  override getRemanentInvoice = async (body: any) => {
    return this._widgetSvc.getRemanentInvoice(
      this._widgetPersonId, this._widgetToken, body
    );
  };

  override verifyPaymentBDV = async (phone: string, amount: string, date: string, reference?: string) => {
    return this._widgetSvc.verifyPaymentBDV(
      this._widgetPersonId, this._widgetToken, phone, amount, date
    );
  };

  override confirmPaymentBDV = async (phone: string, amount: string, reference?: string, date?: string) => {
    return this._widgetSvc.confirmPaymentBDV(
      this._widgetPersonId, this._widgetToken, phone, amount, reference, date
    );
  };

  override getRegister_payment = async (formData: any) => {
    return this._widgetSvc.getRegister_payment(
      this._widgetPersonId, this._widgetToken, formData
    );
  };

  // getUpdate_payment is NOT needed for widget (no edit mode)
  override getUpdate_payment = async (body: any) => {
    throw new Error('Payment update not supported in widget mode');
  };

  // BDV flow: registerPaymentWithToken uses the same register endpoint
  override registerPaymentWithToken = async (paymentData: any, paymentToken: string) => {
    // Build FormData like the regular register flow
    const formData = new FormData();
    formData.append('params', JSON.stringify({ ...paymentData, paymentToken }));
    return this._widgetSvc.getRegister_payment(
      this._widgetPersonId, this._widgetToken, formData
    );
  };
}
